import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Edit, 
  User, 
  Calendar,
  Plus,
  ExternalLink,
  MessageSquare
} from 'lucide-react';
import { format } from 'date-fns';

const severityColors = {
  low: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  critical: 'bg-red-100 text-red-800 border-red-200'
};

const severityIcons = {
  low: <AlertTriangle className="h-4 w-4 text-green-600" />,
  medium: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
  critical: <AlertTriangle className="h-4 w-4 text-red-600" />
};

const KnownIssueCard = ({ issue, onEdit, onClose, onReopen, onIssueClick, currentUser }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      if (newStatus === 'closed') {
        await onClose(issue.id);
      } else {
        await onReopen(issue.id);
      }
    } catch (error) {
      console.error('Error updating issue status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = () => {
    return severityColors[issue.severity] || severityColors.low;
  };

  const isClosed = issue.status === 'closed';

  return (
    <Card 
      className={`w-full cursor-pointer transition-all hover:shadow-md border-l-4 ${
        issue.severity === 'critical' ? 'border-l-red-500' :
        issue.severity === 'medium' ? 'border-l-yellow-500' :
        'border-l-green-500'
      } ${isClosed ? 'opacity-60' : ''}`}
      onClick={() => onIssueClick && onIssueClick(issue)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className={`text-lg flex items-center gap-2 ${isClosed ? 'line-through text-gray-500' : ''}`}>
              {severityIcons[issue.severity]}
              <span className={isClosed ? 'line-through' : ''}>
                {issue.title}
              </span>
            </CardTitle>
            <div className="flex items-center gap-2 mt-2">
              <Badge 
                variant="outline" 
                className={`text-xs ${getSeverityColor()}`}
              >
                {issue.severity.toUpperCase()}
              </Badge>
              {isClosed && (
                <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">
                  CLOSED
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onIssueClick && onIssueClick(issue);
              }}
              className="h-8 w-8 p-0"
              title="View Details"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => e.stopPropagation()}
                  className="h-8 w-8 p-0"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {severityIcons[issue.severity]}
                  Issue Details
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Title</h4>
                  <p className="text-gray-700">{issue.title}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Description</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{issue.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Severity</h4>
                    <Badge className={getSeverityColor()}>
                      {issue.severity.toUpperCase()}
                    </Badge>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Status</h4>
                    <Badge variant={isClosed ? "secondary" : "default"}>
                      {isClosed ? 'CLOSED' : 'OPEN'}
                    </Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Created By</h4>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="h-4 w-4" />
                      {issue.created_by}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Created At</h4>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      {format(new Date(issue.created_at), 'MMM dd, yyyy HH:mm')}
                    </div>
                  </div>
                </div>
                
                {issue.updated_by && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2">Last Updated By</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <User className="h-4 w-4" />
                        {issue.updated_by}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Last Updated At</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(issue.updated_at), 'MMM dd, yyyy HH:mm')}
                      </div>
                    </div>
                  </div>
                )}
                
                {isClosed && issue.closed_by && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2">Closed By</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <XCircle className="h-4 w-4" />
                        {issue.closed_by}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Closed At</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(issue.closed_at), 'MMM dd, yyyy HH:mm')}
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(issue)}
                    disabled={loading}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  
                  {isClosed ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange('open')}
                      disabled={loading || !currentUser}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Reopen
                    </Button>
                  ) : (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleStatusChange('closed')}
                      disabled={loading || !currentUser}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Close Issue
                    </Button>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
          </div>
        </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <User className="h-3 w-3" />
            <span>{issue.created_by}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-3 w-3" />
            <span>{format(new Date(issue.created_at), 'MMM dd, yyyy')}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const KnownIssuesList = ({ 
  issues, 
  onEdit, 
  onClose, 
  onReopen, 
  onCreateIssue,
  onIssueClick,
  currentUser,
  loading = false 
}) => {
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!issues || issues.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <AlertTriangle className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No Known Issues</h3>
          <p className="text-gray-500 text-center mb-4">
            This product doesn't have any known issues yet.
          </p>
          {onCreateIssue && (
            <Button onClick={onCreateIssue} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Create First Issue
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Sort issues: open issues first, then by severity
  const sortedIssues = [...issues].sort((a, b) => {
    if (a.status !== b.status) {
      return a.status === 'open' ? -1 : 1;
    }
    const severityOrder = { critical: 0, medium: 1, low: 2 };
    return severityOrder[a.severity] - severityOrder[b.severity];
  });

  return (
    <div className="space-y-4">
      {/* Header with create button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-slate-600" />
          <h3 className="text-lg font-semibold text-slate-900">Known Issues</h3>
          <Badge variant="outline" className="text-xs">
            {issues.filter(i => i.status === 'open').length} open
          </Badge>
        </div>
        
        {onCreateIssue && (
          <Button onClick={onCreateIssue} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Issue
          </Button>
        )}
      </div>
      
      {/* Scrollable issues container */}
      <div className="max-h-[60vh] overflow-y-auto space-y-4">
        {sortedIssues.map((issue) => (
          <KnownIssueCard
            key={issue.id}
            issue={issue}
            onEdit={onEdit}
            onClose={onClose}
            onReopen={onReopen}
            onIssueClick={onIssueClick}
            currentUser={currentUser}
          />
        ))}
      </div>
    </div>
  );
};

export default KnownIssuesList;